/**
 * PoseTracker - wraps MediaPipe Pose to expose body landmarks (33 keypoints).
 * Uses the video stream already opened by FaceTracker (does not re-request camera).
 * Runs less frequently than face tracking (throttled ~20fps) to save CPU.
 */

export class PoseTracker {
  constructor({ videoEl, onLandmarks }) {
    this.videoEl = videoEl;
    this.onLandmarks = onLandmarks || (() => {});
    this.pose = null;
    this.running = false;
    this._raf = null;
    this._processing = false;
    this._lastSend = 0;
    this._minInterval = 0; // Send next frame as soon as inference finishes
  }

  async start() {
    if (!window.Pose) throw new Error("MediaPipe Pose not loaded");

    const publicUrl = process.env.PUBLIC_URL || "";
    this.pose = new window.Pose({
      locateFile: (file) => `${publicUrl}/models/pose/${file}`,
    });
    this.pose.setOptions({
      modelComplexity: 0,     // lite (fast)
      smoothLandmarks: false, // disable MediaPipe internal buffer lag; smoothed adaptively in StickFigure
      enableSegmentation: false,
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5,
    });
    this.pose.onResults((results) => {
      this.onLandmarks(results.poseLandmarks || null);
    });
    await this.pose.initialize?.();

    this.running = true;
    this._loop();
  }

  _loop = async () => {
    if (!this.running) return;
    const now = performance.now();
    if (!this._processing && this.videoEl.readyState >= 2 && (now - this._lastSend) >= this._minInterval) {
      this._processing = true;
      this._lastSend = now;
      try {
        await this.pose.send({ image: this.videoEl });
      } catch (e) { /* transient */ }
      this._processing = false;
    }
    this._raf = requestAnimationFrame(this._loop);
  };

  stop() {
    this.running = false;
    if (this._raf) cancelAnimationFrame(this._raf);
    if (this.pose) this.pose.close?.();
  }
}
